/**
 * A foto de 5MB do iPhone virando 40KB antes de sair do aparelho.
 *
 * O redimensionamento acontece aqui, e não no servidor, por duas razões: o
 * barbeiro sobe fotos pelo 4G da loja, e uma dependência nativa de imagem no
 * servidor (`sharp`) pesa na imagem Docker e quebra em arquitetura diferente.
 * O servidor não confia no resultado — revalida tudo em `checkImage`.
 *
 * Duas armadilhas de iPhone estão tratadas aqui, e as duas custam uma sessão de
 * depuração se voltarem:
 *
 *  1. **Orientação.** A foto sai deitada com uma etiqueta EXIF dizendo "gire".
 *     O `createImageBitmap` com `imageOrientation: 'from-image'` já entrega
 *     girada; sem isso, todo retrato tirado em pé chega de lado.
 *  2. **WebP no Safari.** Só existe a partir do Safari 14. Quando o canvas não
 *     produz WebP ele devolve PNG silenciosamente — que é enorme. Detectamos e
 *     caímos para JPEG, que comprime parecido e todo mundo lê.
 */

export interface ResizedImage {
  /** Base64 sem o prefixo `data:` — é o que o contrato da API espera. */
  data: string;
  /** Para a prévia na tela, sem uma segunda leitura do arquivo. */
  previewUrl: string;
  width: number;
  height: number;
  bytes: number;
}

export interface ResizeOptions {
  /** Maior lado depois de reduzida. */
  maxDimension: number;
  /** Alvo em bytes; a qualidade cai até caber. */
  maxBytes: number;
}

/** Retratos de ficha e fotos de atendimento. */
export const PHOTO_RESIZE: ResizeOptions = { maxDimension: 1280, maxBytes: 380 * 1024 };

/** Logo e ícone: quadrados, pequenos e nítidos. */
export const BRAND_RESIZE: ResizeOptions = { maxDimension: 512, maxBytes: 240 * 1024 };

function supportsWebp(canvas: HTMLCanvasElement): boolean {
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    // 'from-image' aplica a rotação do EXIF. Sem isto o retrato do iPhone
    // chega deitado — e a pessoa aparece de lado na ficha para sempre.
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Não foi possível ler a imagem'));
      image.src = url;
    });
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toDataUrl(canvas: HTMLCanvasElement, mime: string, quality: number): string {
  return canvas.toDataURL(mime, quality);
}

/**
 * Reduz e comprime até caber no limite.
 *
 * A qualidade desce em degraus em vez de ir direto ao mínimo: quase toda foto
 * cabe já no primeiro, e ninguém precisa olhar para um corte de cabelo cheio de
 * artefato porque o algoritmo se precaveu contra o pior caso.
 */
export async function resizeImage(file: Blob, options: ResizeOptions): Promise<ResizedImage> {
  const source = await loadBitmap(file);

  const sourceWidth = source.width;
  const sourceHeight = source.height;

  if (!sourceWidth || !sourceHeight) {
    throw new Error('Não foi possível ler a imagem');
  }

  const scale = Math.min(1, options.maxDimension / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Este navegador não consegue processar a imagem');

  context.drawImage(source as CanvasImageSource, 0, 0, width, height);
  if ('close' in source) source.close();

  const mime = supportsWebp(canvas) ? 'image/webp' : 'image/jpeg';

  let dataUrl = toDataUrl(canvas, mime, 0.82);
  for (const quality of [0.7, 0.6, 0.5, 0.4]) {
    if (base64Bytes(dataUrl) <= options.maxBytes) break;
    dataUrl = toDataUrl(canvas, mime, quality);
  }

  if (base64Bytes(dataUrl) > options.maxBytes) {
    throw new Error('Não deu para reduzir esta imagem o bastante. Tente outra foto.');
  }

  return {
    data: dataUrl.slice(dataUrl.indexOf(',') + 1),
    previewUrl: dataUrl,
    width,
    height,
    bytes: base64Bytes(dataUrl),
  };
}

/** Tamanho real dos bytes por trás de uma data URL, sem decodificá-la. */
function base64Bytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}
